import { useEffect, useMemo, useRef, useState } from 'react';
import maplibregl, { Map as MlMap, GeoJSONSource } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import type { Feature } from 'geojson';
import { BRAND } from './lib/brand';
import { loadDirectory, STATE_NAMES, type Directory, type DirectoryCourse } from './lib/directory';
import { API_BASE } from './lib/api';
import { useFleet } from './lib/fleet';

/**
 * The operator's console.
 *
 * Built for the person who runs the fleet, not the person playing golf. Where
 * the golfer's app hides everything except one number, this shows the estate:
 * every course in the country, which of them have real hole data, and where the
 * buggies are right now.
 *
 * A globe rather than a flat map, deliberately. The first thing the manager
 * will want to know is "how much of this is actually built" — 1,449 points on a
 * spinning planet answers that in a second in a way a list of names never does,
 * and it is the honest picture: a national footprint with one course lit up
 * where the work has been done.
 */

interface CourseStatus extends DirectoryCourse {
  /** Has a built course record with holes on the backend. */
  built?: boolean;
  holeCount?: number;
}

export default function AdminApp() {
  const el = useRef<HTMLDivElement>(null);
  const map = useRef<MlMap | null>(null);
  const [dir, setDir] = useState<Directory | null>(null);
  const [selected, setSelected] = useState<CourseStatus | null>(null);
  const [checking, setChecking] = useState(false);
  const [query, setQuery] = useState('');
  const [spin, setSpin] = useState(true);

  const fleet = useFleet({ courseId: 'admin', role: 'shop', buggyId: 'ADMIN' });

  useEffect(() => {
    loadDirectory().then(setDir).catch(() => {});
  }, []);

  // --- globe ---------------------------------------------------------------
  useEffect(() => {
    if (!el.current || map.current) return;
    const m = new maplibregl.Map({
      container: el.current,
      style: {
        version: 8,
        // Raster rather than a vector style: no key, no tile budget, and the
        // console has to work on day one on somebody else's laptop.
        sources: {
          sat: {
            type: 'raster',
            tiles: ['https://services.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'],
            tileSize: 256,
            attribution: 'Esri, Maxar, Earthstar Geographics',
          },
        },
        layers: [
          { id: 'bg', type: 'background', paint: { 'background-color': '#040a06' } },
          { id: 'sat', type: 'raster', source: 'sat' },
        ],
      },
      center: [134, -26],
      zoom: 2.4,
      attributionControl: { compact: true },
    });
    map.current = m;
    m.setProjection({ type: 'globe' });
    m.addControl(new maplibregl.NavigationControl({ visualizePitch: true }), 'bottom-right');

    m.on('load', () => {
      m.addSource('courses', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
      // Two layers: a soft halo so a single course is visible at planet zoom,
      // and a hard dot for precision when zoomed in.
      m.addLayer({
        id: 'course-glow',
        type: 'circle',
        source: 'courses',
        paint: {
          'circle-radius': ['interpolate', ['linear'], ['zoom'], 2, 4, 8, 14],
          'circle-color': ['case', ['get', 'built'], '#38e07b', '#ffd23f'],
          'circle-opacity': 0.22,
          'circle-blur': 1,
        },
      });
      m.addLayer({
        id: 'course-dot',
        type: 'circle',
        source: 'courses',
        paint: {
          'circle-radius': ['interpolate', ['linear'], ['zoom'], 2, 1.6, 8, 5],
          'circle-color': ['case', ['get', 'built'], '#38e07b', '#ffd23f'],
          'circle-stroke-color': '#04120a',
          'circle-stroke-width': 0.5,
        },
      });
      m.addSource('fleet', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
      m.addLayer({
        id: 'fleet-dot',
        type: 'circle',
        source: 'fleet',
        paint: {
          'circle-radius': 7,
          'circle-color': '#2f7fff',
          'circle-stroke-color': '#fff',
          'circle-stroke-width': 2,
        },
      });

      m.on('click', 'course-dot', (e) => {
        const f = e.features?.[0];
        if (!f) return;
        setSpin(false);
        setSelected(JSON.parse(f.properties!.raw as string) as CourseStatus);
      });
      m.on('mouseenter', 'course-dot', () => (m.getCanvas().style.cursor = 'pointer'));
      m.on('mouseleave', 'course-dot', () => (m.getCanvas().style.cursor = ''));
    });

    return () => {
      m.remove();
      map.current = null;
    };
  }, []);

  // Idle spin. Stops the moment anyone touches it — a globe that fights the
  // operator is a gimmick, one that presents itself and then gets out of the
  // way is a dashboard.
  useEffect(() => {
    const m = map.current;
    if (!m || !spin) return;
    let raf = 0;
    const tick = () => {
      m.setCenter([m.getCenter().lng + 0.06, m.getCenter().lat]);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    const stop = () => setSpin(false);
    m.on('mousedown', stop);
    m.on('touchstart', stop);
    m.on('wheel', stop);
    return () => {
      cancelAnimationFrame(raf);
      m.off('mousedown', stop);
      m.off('touchstart', stop);
      m.off('wheel', stop);
    };
  }, [spin]);

  // --- points --------------------------------------------------------------
  useEffect(() => {
    const m = map.current;
    if (!m || !dir) return;
    const paint = () => {
      const src = m.getSource('courses') as GeoJSONSource | undefined;
      if (!src) return;
      src.setData({
        type: 'FeatureCollection',
        features: dir.courses.map<Feature>((c) => ({
          type: 'Feature',
          properties: { built: c.holes != null, raw: JSON.stringify(c) },
          geometry: { type: 'Point', coordinates: [c.lng, c.lat] },
        })),
      });
    };
    if (m.isStyleLoaded()) paint();
    else m.once('load', paint);
  }, [dir]);

  useEffect(() => {
    const m = map.current;
    const src = m?.getSource('fleet') as GeoJSONSource | undefined;
    if (!src) return;
    src.setData({
      type: 'FeatureCollection',
      features: Object.values(fleet.positions).map<Feature>((p) => ({
        type: 'Feature',
        properties: { buggyId: p.buggyId },
        geometry: { type: 'Point', coordinates: [p.lng, p.lat] },
      })),
    });
  }, [fleet.positions]);

  // --- selected course -----------------------------------------------------
  useEffect(() => {
    if (!selected || selected.built != null) return;
    setChecking(true);
    fetch(`${API_BASE}/api/courses/${encodeURIComponent(selected.id)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((c: { holes?: unknown[] } | null) => {
        setSelected((s) =>
          s && s.id === selected.id
            ? { ...s, built: !!c?.holes?.length, holeCount: c?.holes?.length ?? 0 }
            : s
        );
      })
      .catch(() => setSelected((s) => (s && s.id === selected.id ? { ...s, built: false } : s)))
      .finally(() => setChecking(false));
  }, [selected]);

  const stats = useMemo(() => {
    if (!dir) return null;
    const byState = new Map<string, number>();
    for (const c of dir.courses) byState.set(c.state, (byState.get(c.state) ?? 0) + 1);
    return {
      total: dir.courses.length,
      withHoleCount: dir.courses.filter((c) => c.holes != null).length,
      states: [...byState.entries()].sort((a, b) => b[1] - a[1]),
    };
  }, [dir]);

  const hits = useMemo(() => {
    if (!dir || query.trim().length < 2) return [];
    const q = query.toLowerCase();
    return dir.courses.filter((c) => c.name.toLowerCase().includes(q)).slice(0, 8);
  }, [dir, query]);

  const flyTo = (c: DirectoryCourse) => {
    setSpin(false);
    setSelected(c);
    setQuery('');
    map.current?.flyTo({ center: [c.lng, c.lat], zoom: 15, duration: 2200 });
  };

  return (
    <div className="admin">
      <div ref={el} className="admin-globe" />

      <div className="admin-panel">
        <div className="admin-brand">
          {BRAND.name} <span>OPERATIONS</span>
        </div>

        <input
          className="admin-search"
          value={query}
          placeholder={dir ? `Search ${dir.count.toLocaleString()} courses` : 'Loading…'}
          onChange={(e) => setQuery(e.target.value)}
        />
        {!!hits.length && (
          <div className="admin-hits">
            {hits.map((c) => (
              <button key={c.id} onClick={() => flyTo(c)}>
                <strong>{c.name}</strong>
                <em>{[c.suburb, STATE_NAMES[c.state] ?? c.state].filter(Boolean).join(' · ')}</em>
              </button>
            ))}
          </div>
        )}

        {stats && (
          <div className="admin-stats">
            <div className="admin-stat">
              <b>{stats.total.toLocaleString()}</b>
              <span>courses mapped</span>
            </div>
            <div className="admin-stat">
              <b>{Object.keys(fleet.positions).length}</b>
              <span>buggies live</span>
            </div>
            <div className="admin-stat">
              <b className={fleet.status === 'open' ? 'ok' : 'bad'}>
                {fleet.status === 'open' ? 'LINKED' : fleet.status.toUpperCase()}
              </b>
              <span>fleet link</span>
            </div>
          </div>
        )}

        {selected ? (
          <div className="admin-card">
            <div className="admin-card-name">{selected.name}</div>
            <div className="admin-card-meta">
              {[selected.suburb, STATE_NAMES[selected.state] ?? selected.state]
                .filter(Boolean)
                .join(' · ')}
            </div>
            <table className="admin-kv">
              <tbody>
                <tr><td>Course id</td><td>{selected.id}</td></tr>
                <tr><td>Position</td><td>{selected.lat.toFixed(5)}, {selected.lng.toFixed(5)}</td></tr>
                <tr>
                  <td>Holes</td>
                  <td>
                    {checking
                      ? 'checking…'
                      : selected.built
                        ? `${selected.holeCount} built`
                        : selected.built === false
                          ? 'not built yet'
                          : '—'}
                  </td>
                </tr>
                {selected.website && (
                  <tr><td>Website</td><td><a href={selected.website} target="_blank" rel="noreferrer">open</a></td></tr>
                )}
                {selected.phone && <tr><td>Phone</td><td>{selected.phone}</td></tr>}
              </tbody>
            </table>
            <div className="admin-note">
              Courses build themselves from OpenStreetMap the first time anyone opens
              them. "Not built yet" means nobody has played it, not that it is unsupported.
            </div>
            <button className="admin-close" onClick={() => setSelected(null)}>Close</button>
          </div>
        ) : (
          <div className="admin-legend">
            <div><i className="dot built" /> hole count known</div>
            <div><i className="dot" /> builds on first open</div>
            <div><i className="dot fleet" /> buggy, live</div>
          </div>
        )}

        {stats && !selected && (
          <div className="admin-states">
            {stats.states.map(([code, n]) => (
              <div key={code}>
                <span>{STATE_NAMES[code] ?? code}</span>
                <b>{n}</b>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
